import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Paper, Stack, Typography } from '@mui/material';

export const EDITOR_EMAIL = 'kvissuet@gmail.com';
const EDITOR_NAME = 'Kevin Vissuet';
const TOKEN_STORAGE_KEY = 'google_editor_id_token';
const USER_STORAGE_KEY = 'google_editor_user';
const GOOGLE_SCRIPT_ID = 'google-identity-services';

const EditorSessionContext = React.createContext({
  user: null,
  signOut: () => {},
});

export function useEditorSession() {
  return React.useContext(EditorSessionContext);
}

function decodeJwt(token) {
  try {
    const [, payload] = token.split('.');
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(normalized));
  } catch (error) {
    return null;
  }
}

export function isValidEditorToken(token) {
  const payload = token ? decodeJwt(token) : null;
  if (!payload?.email || payload.email.toLowerCase() !== EDITOR_EMAIL) return false;
  if (payload.exp && payload.exp * 1000 <= Date.now()) return false;
  return true;
}

function loadGoogleScript() {
  if (window.google?.accounts?.id) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_SCRIPT_ID;
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.addEventListener('load', resolve, { once: true });
    script.addEventListener('error', reject, { once: true });
    document.body.appendChild(script);
  });
}

export function getStoredEditorToken() {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function GoogleEditorGate({ children }) {
  const buttonRef = useRef(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_STORAGE_KEY));
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(USER_STORAGE_KEY) || 'null');
    } catch (error) {
      return null;
    }
  });
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const isEditor = useMemo(() => isValidEditorToken(token), [token]);

  const signOut = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    setToken('');
    setUser(null);
    setStatus(clientId ? 'ready' : 'missing-config');
  }, [clientId]);

  useEffect(() => {
    if (!clientId) {
      setStatus('missing-config');
      return undefined;
    }

    if (token && !isValidEditorToken(token)) {
      signOut();
    }

    let cancelled = false;
    loadGoogleScript()
      .then(() => {
        if (cancelled || !window.google?.accounts?.id) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            const payload = decodeJwt(response.credential);
            if (!payload?.email) {
              setError('Google did not return an email for this account.');
              return;
            }

            if (payload.email.toLowerCase() !== EDITOR_EMAIL) {
              setError('You do not have an account yet. Right now, editing access is only available to Kevin Vissuet.');
              signOut();
              return;
            }

            localStorage.setItem(TOKEN_STORAGE_KEY, response.credential);
            localStorage.setItem(USER_STORAGE_KEY, JSON.stringify({
              email: payload.email,
              name: payload.name,
              picture: payload.picture,
            }));
            setToken(response.credential);
            setUser({
              email: payload.email,
              name: payload.name,
              picture: payload.picture,
            });
            setError('');
            setStatus('authenticated');
          },
        });

        if (buttonRef.current) {
          window.google.accounts.id.renderButton(buttonRef.current, {
            theme: 'outline',
            size: 'large',
            width: Math.min(340, window.innerWidth - 48),
            text: 'signin_with',
            shape: 'pill',
          });
        }
        setStatus(isValidEditorToken(token) ? 'authenticated' : 'ready');
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error');
          setError('Could not load Google sign-in. Check your connection and try again.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, signOut, token]);

  if (isEditor) {
    return (
      <EditorSessionContext.Provider value={{ user, signOut }}>
        {children}
      </EditorSessionContext.Provider>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        bgcolor: '#050505',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper
        elevation={12}
        sx={{
          width: 'min(440px, 100%)',
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
        }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h5" fontWeight={800}>
              Log in
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Editing is currently available only for {EDITOR_NAME}.
            </Typography>
          </Box>

          {status === 'missing-config' && (
            <Alert severity="warning">
              Google auth needs `REACT_APP_GOOGLE_CLIENT_ID` configured before sign-in can work.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}

          <Box ref={buttonRef} sx={{ minHeight: 44 }} />

          {status === 'loading' && (
            <Typography variant="body2" color="text.secondary">
              Loading Google sign-in...
            </Typography>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
